import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tabipla/ui/components/dialog";
import { Textarea } from "@tabipla/ui/components/textarea";
import { toast } from "sonner";
import { useUpdateBookletSpot } from "../../../application/hooks/useBooklets";
import { getCategoryById } from "../../../config/categories";
import type { MekkeBookletSpotDto } from "@tabipla/shared";

interface EditSpotCommentDialogProps {
  bookletId: string;
  bookletSpot: MekkeBookletSpotDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditSpotCommentDialog({
  bookletId,
  bookletSpot,
  open,
  onOpenChange,
}: EditSpotCommentDialogProps) {
  const { t } = useTranslation();
  const updateMutation = useUpdateBookletSpot(bookletId);
  const [comment, setComment] = useState("");

  // Reset comment when dialog opens with new spot
  useEffect(() => {
    if (bookletSpot && open) {
      setComment(bookletSpot.comment || "");
    }
  }, [bookletSpot, open]);

  const handleSave = async () => {
    if (!bookletSpot) return;

    try {
      await updateMutation.mutateAsync({
        spotId: bookletSpot.spotId,
        comment: comment.trim() || null,
      });
      toast.success(t("booklets.commentUpdated"));
      onOpenChange(false);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  if (!bookletSpot) return null;

  const spot = bookletSpot.spot;
  const category = getCategoryById(spot.category);
  const CategoryIcon = category.icon;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("booklets.editComment")}</DialogTitle>
        </DialogHeader>

        {/* Spot Info */}
        <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
          {spot.images && spot.images.length > 0 ? (
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg">
              <img
                src={spot.images[0].url}
                alt={spot.name}
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${category.color}15` }}
            >
              <CategoryIcon className="h-6 w-6" style={{ color: category.color }} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-medium">{spot.name}</h3>
            {spot.address && (
              <p className="truncate text-sm text-muted-foreground">{spot.address}</p>
            )}
          </div>
        </div>

        {/* Comment Input */}
        <div>
          <label className="mb-2 block text-sm font-medium">{t("booklets.comment")}</label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("booklets.commentPlaceholder")}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
